import { useState } from 'react';
import { useGa4Dashboard } from '../hooks/useAnalyticsDashboard';
import LineChart from '../components/analytics/LineChart';
import ProjectContextBadge from '../components/layout/ProjectContextBadge';

const PERIODS = [
  { value: 14, label: '2 Wochen' },
  { value: 28, label: '4 Wochen' },
  { value: 56, label: '2 Monate' },
];

function Kpi({ label, value, sub, accent = '#C2410C' }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-4">
      <div className="text-[10.5px] font-semibold tracking-wider text-gray-400 uppercase">{label}</div>
      <div className="text-[24px] font-bold mt-1.5" style={{ color: accent }}>{value ?? '—'}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
      <h3 className="text-[13.5px] font-semibold text-gray-800 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function TopList({ rows, labelKey = 'key', valueKey = 'sessions', valueFormat = (v) => v }) {
  if (!rows || rows.length === 0) {
    return <div className="text-[11.5px] text-gray-400 py-3 text-center">Keine Daten.</div>;
  }
  const max = Math.max(...rows.map((r) => Number(r[valueKey]) || 0), 1);
  return (
    <div className="space-y-2">
      {rows.map((r, i) => {
        const v = Number(r[valueKey]) || 0;
        const pct = (v / max) * 100;
        return (
          <div key={`${r[labelKey]}-${i}`}>
            <div className="flex items-baseline justify-between text-[11.5px] mb-0.5">
              <span className="text-gray-700 font-medium truncate">{r[labelKey] || '—'}</span>
              <span className="text-gray-500 tabular-nums">{valueFormat(v)}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: '#C2410C' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatDuration(seconds) {
  if (!seconds) return '0s';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export default function AnalyticsGa4() {
  const [days, setDays] = useState(28);
  const { data, isLoading, error } = useGa4Dashboard(days);

  return (
    <div>
      <div className="flex items-start justify-between mb-5">
        <div>
          <ProjectContextBadge />
          <h1 className="text-[23px] font-bold text-gray-900">Google Analytics</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Traffic, Engagement und Conversions aus GA4</p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setDays(p.value)}
              className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors ${
                days === p.value ? 'bg-orange-100 text-orange-700' : 'text-gray-500 hover:bg-gray-50'
              }`}
              style={days === p.value ? { backgroundColor: '#FFEDD5', color: '#C2410C' } : {}}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-12 text-center text-[13px] text-gray-400">
          Lade GA4-Daten…
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-[12.5px] text-red-700">
          <div className="font-semibold mb-1">GA4-Daten konnten nicht geladen werden</div>
          <div>{error.response?.data?.message || error.message}</div>
        </div>
      )}

      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <Kpi label="Sessions" value={data.summary?.sessions?.toLocaleString('de')} />
            <Kpi label="Users" value={data.summary?.activeUsers?.toLocaleString('de')} sub={`${data.summary?.newUsers || 0} neu`} />
            <Kpi label="Engagement Rate" value={`${Math.round((data.summary?.engagementRate || 0) * 100)}%`} />
            <Kpi label="Ø Sitzungsdauer" value={formatDuration(data.summary?.avgSessionDuration || 0)} />
            <Kpi label="Conversions" value={Math.round(data.summary?.conversions || 0)} />
          </div>

          {/* Linechart */}
          <Section title="Sessions & Users — Verlauf">
            <LineChart
              series={[
                {
                  name: 'Sessions',
                  color: '#C2410C',
                  points: (data.trend || []).map((d) => ({ date: d.date, value: d.sessions })),
                },
                {
                  name: 'Users',
                  color: '#F59E0B',
                  points: (data.trend || []).map((d) => ({ date: d.date, value: d.activeUsers })),
                },
              ]}
            />
          </Section>

          {/* Grids */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
            <Section title="Top-Kanäle">
              <TopList
                rows={data.channels || []}
                valueKey="sessions"
                valueFormat={(v) => `${v.toLocaleString('de')} Sessions`}
              />
            </Section>
            <Section title="Devices">
              <TopList
                rows={data.devices || []}
                valueKey="sessions"
                valueFormat={(v) => v.toLocaleString('de')}
              />
            </Section>
            <Section title="Top-Seiten">
              <TopList
                rows={data.top_pages || []}
                valueKey="screenPageViews"
                valueFormat={(v) => `${v.toLocaleString('de')} Views`}
              />
            </Section>
            <Section title="Top-Landing-Pages">
              <TopList
                rows={data.top_landing_pages || []}
                valueKey="sessions"
                valueFormat={(v) => `${v.toLocaleString('de')} Sessions`}
              />
            </Section>
          </div>
        </>
      )}
    </div>
  );
}
